import { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader } from "../../components/PageHeader";
import { formatCurrency, formatDateShort } from "../Obras/obraFormat";
import {
  getBankAccounts,
  getBankSyncRuns,
  getBankSyncSettings,
  syncBankMovements,
  updateBankAccount,
  type BankAccount,
  type BankSyncRun,
  type BankSyncSettings,
} from "../../bancos/bancosApi";
import { getCompanies, type Company } from "../../companies/companiesApi";

const selectClass =
  "px-3 py-2 border border-border rounded bg-background text-foreground text-sm";

const FLOATING_BUTTON_CLEARANCE = 88;

/** Tipos como o BudgetBakers os escreve, em português para quem lê o ecrã. */
const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  CurrentAccount: "Conta à ordem",
  SavingAccount: "Conta poupança",
  CreditCard: "Cartão de crédito",
  Cash: "Numerário",
  General: "Geral",
  Investment: "Investimento",
  Loan: "Empréstimo",
  Mortgage: "Crédito habitação",
  Overdraft: "Descoberto",
  Insurance: "Seguro",
  Bonus: "Bónus",
};

function describeAccountType(accountType: string | null): string {
  if (!accountType) return "—";
  return ACCOUNT_TYPE_LABELS[accountType] ?? accountType;
}

/** Quanto durou uma passagem. Uma sem fim é uma que foi interrompida, e diz-se isso. */
function formatDuration(startedAt: string, finishedAt: string | null): string {
  if (!finishedAt) return "por terminar";

  const milliseconds =
    new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(milliseconds) || milliseconds < 0) return "";
  if (milliseconds < 1000) return "menos de 1 segundo";

  const seconds = Math.round(milliseconds / 1000);
  if (seconds < 60) return `${seconds} segundos`;

  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest === 0 ? `${minutes} min` : `${minutes} min ${rest} s`;
}

/** O que aconteceu naquela passagem, em linguagem de quem lê e não de quem programa. */
function describeRun(run: BankSyncRun): string {
  if (!run.finishedAt) {
    return "Interrompida antes de terminar.";
  }

  if (!run.succeeded) {
    return run.error ?? "Falhou.";
  }

  const parts: string[] = [];
  if (run.movementsCreated > 0) {
    parts.push(`${run.movementsCreated} transações novas`);
  }
  if (run.movementsUpdated > 0) {
    parts.push(`${run.movementsUpdated} actualizadas`);
  }
  if (run.suppliersMatched > 0) {
    parts.push(`${run.suppliersMatched} com fornecedor reconhecido`);
  }
  if (run.clientsMatched > 0) {
    parts.push(`${run.clientsMatched} com cliente reconhecido`);
  }
  if (run.movementsPendingRecognition > 0) {
    parts.push(`${run.movementsPendingRecognition} ainda por reconhecer`);
  }
  if (run.movementsAutoReconciled > 0) {
    parts.push(`${run.movementsAutoReconciled} reconciliadas sozinhas`);
  }

  return parts.length === 0 ? "Sem novidades." : `${parts.join(", ")}.`;
}

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return `${formatDateShort(value)} às ${date.toLocaleTimeString("pt-PT", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

export function BancosView() {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [settings, setSettings] = useState<BankSyncSettings | null>(null);
  const [runs, setRuns] = useState<BankSyncRun[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [loadedAccounts, loadedSettings, loadedCompanies, loadedRuns] =
        await Promise.all([
          getBankAccounts(),
          getBankSyncSettings(),
          getCompanies(),
          getBankSyncRuns(),
        ]);
      setAccounts(loadedAccounts);
      setSettings(loadedSettings);
      setCompanies(loadedCompanies);
      setRuns(loadedRuns);
      setError(null);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Não foi possível carregar as contas.",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSync = useCallback(async () => {
    setIsSyncing(true);
    setNotice(null);
    setError(null);

    try {
      const result = await syncBankMovements();

      if (!result.succeeded) {
        setError(result.error ?? "A sincronização falhou.");
        return;
      }

      const parts = [
        `${result.movementsCreated} transações novas`,
        `${result.movementsUpdated} actualizadas`,
      ];

      if (result.suppliersMatched > 0) {
        parts.push(`${result.suppliersMatched} com fornecedor reconhecido`);
      }
      if (result.clientsMatched > 0) {
        parts.push(`${result.clientsMatched} com cliente reconhecido`);
      }

      if (result.movementsAutoReconciled > 0) {
        parts.push(`${result.movementsAutoReconciled} reconhecidas e reconciliadas sozinhas`);
      }

      let message = `Sincronizado: ${parts.join(", ")}.`;

      if (result.legacyMovementsInWindow > 0) {
        message += ` Atenção: há ${result.legacyMovementsInWindow} movimentos importados de extratos no mesmo período, que podem estar repetidos.`;
      }

      setNotice(message);
      await load();
    } catch (syncError) {
      setError(
        syncError instanceof Error
          ? syncError.message
          : "Não foi possível sincronizar.",
      );
    } finally {
      setIsSyncing(false);
    }
  }, [load]);

  const handleCompanyChange = useCallback(
    async (accountId: number, companyId: number | null) => {
      setAccounts((current) =>
        current.map((account) =>
          account.id === accountId
            ? {
                ...account,
                companyId,
                companyName:
                  companies.find((company) => company.id === companyId)?.name ??
                  null,
              }
            : account,
        ),
      );

      try {
        await updateBankAccount(accountId, companyId);
      } catch (updateError) {
        setError(
          updateError instanceof Error
            ? updateError.message
            : "Não foi possível guardar a empresa da conta.",
        );
        await load();
      }
    },
    [companies, load],
  );

  // Saldos somados por moeda: somar contas em moedas diferentes daria um número sem significado.
  const balancesByCurrency = useMemo(() => {
    const totals = new Map<string, number>();

    for (const account of accounts) {
      if (account.isArchived || account.currentBalance == null) continue;
      totals.set(
        account.currencyCode,
        (totals.get(account.currencyCode) ?? 0) + account.currentBalance,
      );
    }

    return Array.from(totals.entries());
  }, [accounts]);

  const connectionState = useMemo(() => {
    if (!settings) return null;

    if (!settings.hasApiToken) {
      return {
        tone: "warning" as const,
        text: "A ligação ao BudgetBakers ainda não está configurada. Vai a Definições → Bancos e cola o token de acesso.",
      };
    }

    if (settings.lastSyncError) {
      return {
        tone: "error" as const,
        text: `Última sincronização falhou: ${settings.lastSyncError}`,
      };
    }

    if (!settings.lastSyncedAt) {
      return {
        tone: "warning" as const,
        text: "Token guardado, mas ainda nunca se sincronizou. Carrega em Sincronizar agora.",
      };
    }

    return {
      tone: "ok" as const,
      text: `Última sincronização a ${formatDateTime(settings.lastSyncedAt)}${
        settings.enabled
          ? `, a repetir de ${settings.pollIntervalMinutes} em ${settings.pollIntervalMinutes} minutos.`
          : ". A sincronização automática está desligada."
      }`,
    };
  }, [settings]);

  return (
    // A folga no fim é pelo botão do assistente, que é fixo no canto inferior direito (48px de
    // lado, a 24px do fundo) e tapava a última linha do histórico.
    <div className="p-6 space-y-4" style={{ paddingBottom: FLOATING_BUTTON_CLEARANCE }}>
      <PageHeader
        title="Bancos"
        subtitle="Contas do grupo, saldos e estado da ligação ao banco."
        primaryAction={{
          label: isSyncing ? "A sincronizar…" : "Sincronizar agora",
          onClick: () => void handleSync(),
          disabled: isSyncing || !settings?.hasApiToken,
        }}
      />

      {connectionState && (
        <div
          className={
            connectionState.tone === "error"
              ? "border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
              : connectionState.tone === "warning"
                ? "border border-border bg-yellow-500/10 text-yellow-700 px-4 py-3 text-sm"
                : "border border-border bg-muted px-4 py-3 text-sm"
          }
          style={{ borderRadius: "var(--radius)" }}
          role={connectionState.tone === "error" ? "alert" : undefined}
        >
          {connectionState.text}
        </div>
      )}

      {/* A live region fica sempre montada (só o texto entra e sai): montá-la já com texto
          falha o anúncio em alguns leitores de ecrã. */}
      <div aria-live="polite">
        {notice && (
          <div
            className="border border-border bg-muted px-4 py-2 text-sm"
            style={{ borderRadius: "var(--radius)" }}
          >
            {notice}
          </div>
        )}
      </div>

      {error && (
        <div
          className="border border-border bg-destructive/10 text-destructive px-4 py-3 text-sm"
          style={{ borderRadius: "var(--radius)" }}
          role="alert"
        >
          {error}
        </div>
      )}

      {balancesByCurrency.length > 0 && (
        <div className="flex flex-wrap gap-4">
          {balancesByCurrency.map(([currency, total]) => (
            <div
              key={currency}
              className="bg-card border border-border px-4 py-3"
              style={{ borderRadius: "var(--radius-card)", minWidth: 220 }}
            >
              <div className="text-sm text-secondary-text">
                Saldo total {currency !== "EUR" ? `(${currency})` : ""}
              </div>
              {/* Tamanho em estilo: o index.css é pré-compilado e não traz text-lg. */}
              <div style={{ fontSize: "1.125rem" }}>{formatCurrency(total)}</div>
            </div>
          ))}
        </div>
      )}

      <div
        className="bg-card border border-border overflow-x-auto"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <table className="w-full" style={{ minWidth: 1080 }}>
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th scope="col" className="px-4 py-3">Conta</th>
              <th scope="col" className="px-4 py-3" style={{ width: 210 }}>IBAN</th>
              <th scope="col" className="px-4 py-3" style={{ width: 150 }}>Tipo</th>
              <th scope="col" className="px-4 py-3 text-right" style={{ width: 140 }}>Saldo</th>
              <th scope="col" className="px-4 py-3 text-right" style={{ width: 110 }}>Movimentos</th>
              <th scope="col" className="px-4 py-3" style={{ width: 130 }}>Último</th>
              <th scope="col" className="px-4 py-3" style={{ width: 200 }}>Empresa</th>
              <th scope="col" className="px-4 py-3" style={{ width: 170 }}>Sincronizada</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-secondary-text">
                  A carregar…
                </td>
              </tr>
            ) : accounts.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-secondary-text">
                  Ainda não há contas. Configura o token em Definições → Bancos e
                  carrega em Sincronizar agora.
                </td>
              </tr>
            ) : (
              accounts.map((account) => (
                <tr key={account.id} className="border-b border-border last:border-b-0">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {account.color && (
                        <span
                          aria-hidden="true"
                          style={{
                            backgroundColor: account.color,
                            borderRadius: 9999,
                            display: "inline-block",
                            height: 10,
                            width: 10,
                          }}
                        />
                      )}
                      <span>{account.name}</span>
                    </div>
                    <div className="text-sm text-secondary-text">
                      {account.isArchived && "Arquivada. "}
                      {account.isBankSync
                        ? "Ligada ao banco"
                        : "Sem ligação automática ao banco"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {account.bankAccountNumber ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {describeAccountType(account.accountType)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {account.currentBalance == null
                      ? "—"
                      : formatCurrency(account.currentBalance)}
                  </td>
                  <td className="px-4 py-3 text-right">{account.movementCount}</td>
                  <td className="px-4 py-3 text-sm">
                    {account.lastMovementDate
                      ? formatDateShort(account.lastMovementDate)
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {/* O cabeçalho da coluna já diz "Empresa"; o aria-label acrescenta de que
                        conta se trata, que é o que falta a quem ouve a linha fora de contexto. */}
                    <select
                      aria-label={`Empresa da conta ${account.name}`}
                      className={selectClass}
                      value={account.companyId ?? ""}
                      onChange={(event) =>
                        void handleCompanyChange(
                          account.id,
                          event.target.value === ""
                            ? null
                            : Number(event.target.value),
                        )
                      }
                    >
                      <option value="">Por atribuir</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-sm text-secondary-text">
                    {formatDateTime(account.lastSyncedAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Histórico: quando é que os dados foram actualizados, e desde quando é que não são. */}
      <div
        className="bg-card border border-border"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <div className="px-4 py-3 border-b border-border">
          <span>Histórico de sincronizações</span>
          <span className="text-secondary-text text-sm">
            {" "}
            · as {runs.length > 0 ? runs.length : "últimas"} mais recentes
          </span>
        </div>

        {runs.length === 0 ? (
          <div className="px-4 py-6 text-center text-secondary-text">
            Ainda não há nenhuma sincronização registada.
          </div>
        ) : (
          <ul className="px-4 py-2">
            {runs.map((run) => (
              <li
                key={run.id}
                className="flex items-start gap-3 py-2 border-b border-border last:border-b-0"
              >
                <span
                  aria-hidden="true"
                  style={{
                    backgroundColor: !run.finishedAt
                      ? "var(--pill-warning-text, #8a4708)"
                      : run.succeeded
                        ? "var(--color-success, #16a34a)"
                        : "var(--color-destructive, #b10d1b)",
                    borderRadius: 9999,
                    display: "inline-block",
                    flexShrink: 0,
                    height: 8,
                    marginTop: 7,
                    width: 8,
                  }}
                />
                <div className="flex flex-col gap-1">
                  <span className="text-sm">
                    {formatDateTime(run.startedAt)}
                    <span className="text-secondary-text">
                      {" · "}
                      {run.trigger === "automatic" ? "automática" : "manual"}
                      {run.triggeredBy ? ` por ${run.triggeredBy}` : ""}
                      {formatDuration(run.startedAt, run.finishedAt)
                        ? ` · ${formatDuration(run.startedAt, run.finishedAt)}`
                        : ""}
                    </span>
                  </span>
                  <span
                    className={
                      run.finishedAt && !run.succeeded
                        ? "text-destructive text-sm"
                        : "text-secondary-text text-sm"
                    }
                  >
                    {describeRun(run)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
